/**
 * Shared utility functions for appointment components.
 * Used by appointments-calendar and appointments-table.
 */

import { Observable } from 'rxjs';

export function mapEmployeesToStaffOptions(
  employees: {
    id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    emp_id?: string;
  }[]
): { label: string; value: string }[] {
  return employees.map(employee => {
    const name = `${employee.first_name ?? ''} ${employee.last_name ?? ''}`.trim();
    return {
      label: name || employee.email || employee.emp_id || 'Staff',
      value: employee.id,
    };
  });
}

/**
 * Format a duration object into a human-readable string (e.g. "1 hour 30 minutes").
 * Returns 'N/A' for missing or empty duration.
 */
export function formatDuration(
  duration: { minutes?: number; hours?: number } | null | undefined
): string {
  if (!duration) return 'N/A';
  const { hours, minutes } = duration;
  if (hours && minutes) {
    return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
  if (hours) return `${hours} hour${hours > 1 ? 's' : ''}`;
  if (minutes) return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  return 'N/A';
}

/**
 * Run reschedule update API and handle success/error (toast, close modal, reload).
 * Shared by appointments-table and appointments-calendar onRescheduleUpdate.
 */
export function runRescheduleUpdate(
  data: { appointmentId: string; payload: any },
  appointmentService: { updateAppointment(id: string, payload: any): Observable<any> },
  messageService: {
    add(msg: { severity?: string; summary?: string; detail?: string; [key: string]: any }): void;
  },
  callbacks: {
    setLoading: (value: boolean) => void;
    closeRescheduleModal: () => void;
    loadAppointments: () => void;
  }
): void {
  callbacks.setLoading(true);
  appointmentService.updateAppointment(data.appointmentId, data.payload).subscribe({
    next: () => {
      messageService.add({
        severity: 'success',
        summary: 'Appointment changed!',
        detail: 'The appointment has been rescheduled successfully.',
      });
      callbacks.setLoading(false);
      callbacks.closeRescheduleModal();
      callbacks.loadAppointments();
    },
    error: () => {
      callbacks.setLoading(false);
      callbacks.closeRescheduleModal();
    },
  });
}
